import styled from "styled-components";
import { useContext, useState } from "react";
import { useDispatch } from "react-redux";
import { cartActions } from "../../store/cart-slice";
import { ProductContext } from "../../context/ProductContext";
import AddToCartAnim from "../animations/AddToCartAnim";
import { useNavigation } from "../../utils/navigation";
import { useNavigate } from "react-router-dom";
import { calculatePrice } from "../../utils/discount";
import { useTranslation } from "react-i18next";

const Container = styled.figure`
  position: relative;
  background-color: var(--bg-200);
  width: 100%;
  padding: 0;
  margin: 0;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  gap: var(--spacing-sm);
  padding: var(--spacing-xl) 0 0 0;
  box-shadow: var(--shadow-medium);
  border-radius: 8px;
`;
const WrapperBottom = styled.div`
  width: 100%;
`;
const Picture = styled.picture`
  display: flex; // Omogućavanje flex layout-a za centriranje slike
  align-items: center;
  justify-content: center;
  //height: 240px;
  cursor: pointer;
  width: 92%;
  overflow: hidden;

  height: 140px;
  max-height: 140px;
  min-height: 140px;
  position: relative;
  background-color: var(--bg-100);
  border-radius: var(--border-radius-base);

  &:hover {
    background-size: cover;
    background-position: center;
    background-repeat: no-repeat;
    .primary-image {
      display: none;
      display: ${(props) => (props.$secondary_image ? "none" : "block")};
    }

    .secondary-image {
      display: block;
      display: ${(props) => (props.$secondary_image ? "block" : "none")};
    }
  }
  @media (min-width: 768px) {
    height: 200px;
    max-height: 200px;
    min-height: 200px;
  }
`;

const Image = styled.img`
  max-width: 88%;
  max-height: 88%;
  width: auto;
  height: auto;

  object-fit: contain;
  display: block;
  margin: 0 auto;
  opacity: ${({ $outOfStock }) => ($outOfStock ? 0.45 : 1)};
  filter: ${({ $outOfStock }) => ($outOfStock ? "grayscale(1)" : "none")};
  &.secondary-image {
    display: none;
  }
`;

const OutOfStockBadge = styled.span`
  position: absolute;
  top: var(--spacing-sm);
  left: var(--spacing-sm);
  z-index: 1;
  padding: 3px 10px;
  border-radius: 999px;
  background-color: var(--bg-100);
  border: 1px solid var(--bg-300);
  color: var(--text-200);
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
`;
const Bottom = styled.div`
  width: 90%;
`;

const ProductTitle = styled.h5`
  margin: 12px 0 0 0;
  color: var(--text-100);
  height: calc(var(--font-size-large) * 2 + 6px);
  cursor: pointer;
  &:hover {
    color: var(--primary-200);
  }
  text-align: center;
  width: 100%;
  @media (min-width: 768px) {
    font-size: var(--font-size-large);
  }
`;

const DropdownContainer = styled.div`
  width: 100%;
  position: relative;
  display: grid;
  @media (max-width: 768px) {
    grid-template-rows: 1fr 1fr;
  }
  @media (min-width: 768px) {
    grid-template-columns: 2fr 1fr;
  }
`;

const Select = styled.select`
  font-size: var(--font-size-base);
  //padding: 8px;
  color: var(--text-200);
  border: 1px solid var(--text-200);
  border: none;
  background-color: var(--bg-100);
  cursor: pointer;
  //border-radius: 4px;
  //border-radius: 12px 12px 0 0px;
  &:focus {
    outline: none;
    border-color: var(--accent-100);
  }
  overflow: hidden;
  @media (min-width: 768px) {
    //border-radius: 12px 0 0 12px;
  }
  width: 100%;
  max-width: 100%;
  text-align: center;
`;

const Option = styled.option`
  font-size: var(--font-size-large);
  color: var(--text-200);
  background-color: var(--bg-200);
`;
const FlexDiv = styled.div`
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 12px;
`;
const ButtonWrapper = styled.div`
  position: relative; /* Roditeljski kontejner koji omogućava apsolutno pozicioniranje za decu */
  display: inline-block; /* Omogućava kontejneru da se prilagodi veličini sadržaja */
  width: 100%;
  text-align: center;
  //margin-top: 14px;
`;
const Button = styled.button`
  width: 100%;
  text-transform: capitalize;
  z-index: 1;
  font-size: var(--font-size-base);

  //border-radius: 0 0 12px 12px;
  @media (min-width: 768px) {
    //border-radius: 0 12px 12px 0;
  }
  border-radius: 0;
`;

const ExtraTextDesktop = styled.span`
  display: none;
  font-weight: 100;
  @media (min-width: 768px) {
    display: inline;
  }
`;

const ProductCard = ({ product }) => {
  const { t } = useTranslation();
  const [selectedQuantity, setSelectedQuantity] = useState(10);
  const dispatch = useDispatch();
  const { setIsCartOpen, maxAttributes, currencyTag } =
    useContext(ProductContext);
  const { goToProduct } = useNavigation();
  const navigate = useNavigate();

  const [isAnimating, setIsAnimating] = useState(false);

  const addToCart = (selectedQuantity) => {
    dispatch(
      cartActions.addToCart({
        product: product,
        quantity: selectedQuantity,
      })
    );
    setIsCartOpen(true);
    setIsAnimating(true);
  };

  const handleAnimationComplete = () => {
    setIsAnimating(false);
  };

  const discountedPrice = calculatePrice(product.price, selectedQuantity);

  // Calculate savings
  const savingsPercentage = Math.round(
    ((product.price - discountedPrice) / product.price) * 100
  );

  const primaryImage = product.images?.find((img) => img.is_primary);
  const secondaryImage = product.images?.find((img) => !img.is_primary);
  const isOutOfStock = product.is_in_stock === "out_of_stock";

  const handleChange = (e) => {
    setSelectedQuantity(Number(e.target.value));
  };
  return (
    <Container>
      <Picture
        onClick={() =>
          navigate(goToProduct(product.category_name, product.name))
        }
        $secondary_image={secondaryImage}
      >
        {isOutOfStock && (
          <OutOfStockBadge>{t("BUTTONS.AVAILABLE_SOON")}</OutOfStockBadge>
        )}
        {primaryImage && (
          <Image
            src={primaryImage.thumbnail}
            alt={`${product.name}`}
            srcSet={`${primaryImage.thumbnail} 320w, ${primaryImage.large} 480w, ${primaryImage.large} 800w`}
            sizes="(max-width: 320px) 300px, (max-width: 480px) 440px, 800px"
            loading="lazy"
            className="primary-image"
            $outOfStock={isOutOfStock}
          />
        )}
        {secondaryImage && (
          <Image
            src={secondaryImage.thumbnail}
            alt={`${product.name}`}
            srcSet={`${secondaryImage.thumbnail} 320w, ${secondaryImage.large} 480w, ${secondaryImage.large} 800w`}
            sizes="(max-width: 320px) 300px, (max-width: 480px) 440px, 800px"
            loading="lazy"
            className="secondary-image"
            $outOfStock={isOutOfStock}
          />
        )}
      </Picture>
      <Bottom>
        <ProductTitle
          onClick={() =>
            navigate(goToProduct(product.category_name, product.name))
          }
        >
          {product.name}
        </ProductTitle>
        <FlexDiv style={{ marginTop: "4px" }}>
          <h3 style={{ color: "var(--accent-200)" }}>
            {currencyTag}
            {discountedPrice.toFixed(2)}/{t("PRODUCT_CARD.PIECE")}
          </h3>
          <h4 style={{ fontWeight: 100 }}>
            {currencyTag}
            {(discountedPrice * selectedQuantity).toFixed(2)}
          </h4>
        </FlexDiv>
        <FlexDiv style={{ marginTop: "4px" }}>
          <h4>
            <span style={{ marginRight: "4px" }}>MSRP</span>
            {currencyTag}
            {product.price}
          </h4>
          <h4 style={{ fontWeight: 100, textDecoration: "line-through" }}>
            {currencyTag}
            {(product.price * selectedQuantity).toFixed(2)}
          </h4>
        </FlexDiv>
      </Bottom>
      <DropdownContainer>
        <Select value={selectedQuantity} onChange={handleChange}>
          {[1, 10, 30, 50].map((item) => (
            <Option key={item} value={item}>
              {selectedQuantity === item ? (
                <strong>{`${item} cans`}</strong>
              ) : (
                <>
                  <strong>{`${item} cans`}</strong>{" "}
                  <ExtraTextDesktop>
                    ( {currencyTag}
                    {calculatePrice(product.price, item)} / can)
                  </ExtraTextDesktop>
                </>
              )}
            </Option>
          ))}
        </Select>
        <ButtonWrapper>
          <Button
            onClick={() => addToCart(selectedQuantity)}
            disabled={isOutOfStock}
          >
            <svg
              width="20px"
              height="20px"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                fill-rule="evenodd"
                clip-rule="evenodd"
                d="M14 2C14 1.44772 13.5523 1 13 1C12.4477 1 12 1.44772 12 2V8.58579L9.70711 6.29289C9.31658 5.90237 8.68342 5.90237 8.29289 6.29289C7.90237 6.68342 7.90237 7.31658 8.29289 7.70711L12.2929 11.7071C12.6834 12.0976 13.3166 12.0976 13.7071 11.7071L17.7071 7.70711C18.0976 7.31658 18.0976 6.68342 17.7071 6.29289C17.3166 5.90237 16.6834 5.90237 16.2929 6.29289L14 8.58579V2ZM1 3C1 2.44772 1.44772 2 2 2H2.47241C3.82526 2 5.01074 2.90547 5.3667 4.21065L5.78295 5.73688L7.7638 13H18.236L20.2152 5.73709C20.3604 5.20423 20.9101 4.88998 21.4429 5.03518C21.9758 5.18038 22.29 5.73006 22.1448 6.26291L20.1657 13.5258C19.9285 14.3962 19.1381 15 18.236 15H8V16C8 16.5523 8.44772 17 9 17H16.5H18C18.5523 17 19 17.4477 19 18C19 18.212 18.934 18.4086 18.8215 18.5704C18.9366 18.8578 19 19.1715 19 19.5C19 20.8807 17.8807 22 16.5 22C15.1193 22 14 20.8807 14 19.5C14 19.3288 14.0172 19.1616 14.05 19H10.95C10.9828 19.1616 11 19.3288 11 19.5C11 20.8807 9.88071 22 8.5 22C7.11929 22 6 20.8807 6 19.5C6 18.863 6.23824 18.2816 6.63048 17.8402C6.23533 17.3321 6 16.6935 6 16V14.1339L3.85342 6.26312L3.43717 4.73688C3.31852 4.30182 2.92336 4 2.47241 4H2C1.44772 4 1 3.55228 1 3ZM16 19.5C16 19.2239 16.2239 19 16.5 19C16.7761 19 17 19.2239 17 19.5C17 19.7761 16.7761 20 16.5 20C16.2239 20 16 19.7761 16 19.5ZM8 19.5C8 19.2239 8.22386 19 8.5 19C8.77614 19 9 19.2239 9 19.5C9 19.7761 8.77614 20 8.5 20C8.22386 20 8 19.7761 8 19.5Z"
                fill="var(--bg-100)"
              />
            </svg>
          </Button>
          {isAnimating && (
            <AddToCartAnim
              isAnimating={isAnimating}
              onComplete={handleAnimationComplete}
            />
          )}
        </ButtonWrapper>
      </DropdownContainer>
    </Container>
  );
};

export default ProductCard;
